/**
 * SQLite 词汇数据库接口 - V2版本
 * 支持多源词汇查询，保留所有词库来源信息
 */

// @ts-ignore
import initSqlJs from 'sql.js';

interface WordResult {
  word: string;
  translation: string;
  usphone: string;
  ukphone: string;
  pronunciation: string;
  content_type: string;
  level: string;
  source_file: string;
  all_sources?: string[];  // 所有来源文件
  source_count?: number;    // 来源数量
}

interface WordSource {
  source_file: string;
  translation: string;
  usphone: string;
  ukphone: string;
  pronunciation: string;
  content_type: string;
  level: string;
  priority: number;
}

class VocabularyDBV2 {
  private db: any = null;
  private initialized = false;
  private initPromise: Promise<void> | null = null;
  
  // 简单的LRU缓存，避免频繁查询
  private cache = new Map<string, WordResult | null>();
  private sourcesCache = new Map<string, WordSource[]>();  // 缓存词汇的所有来源
  private maxCacheSize = 500; // 只缓存500个最常查询的词

  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._doInitialize();
    return this.initPromise;
  }

  private async _doInitialize(): Promise<void> {
    try {
      console.log('🔄 Initializing vocabulary database V2...');
      
      // 初始化 sql.js
      const SQL = await initSqlJs({
        locateFile: (file: string) => {
          if (file.endsWith('.wasm')) {
            return `/sql-wasm.wasm`;
          }
          return `/${file}`;
        }
      });
      
      // 加载数据库文件
      const response = await fetch('/vocabulary-v2.db');
      if (!response.ok) {
        throw new Error(`Failed to load vocabulary-v2.db: ${response.status}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      this.db = new SQL.Database(new Uint8Array(arrayBuffer));
      
      // 验证数据库
      const result = this.db.exec('SELECT COUNT(*) as count FROM words')[0];
      const wordCount = result.values[0][0];
      
      const sourcesResult = this.db.exec('SELECT COUNT(*) as count FROM word_sources')[0];
      const sourcesCount = sourcesResult.values[0][0];
      
      this.initialized = true;
      console.log(`✅ Vocabulary database V2 initialized`);
      console.log(`   📚 ${wordCount} unique words`);
      console.log(`   📋 ${sourcesCount} source records`);
      
    } catch (error) {
      console.error('❌ Failed to initialize vocabulary database V2:', error);
      this.initialized = false;
      this.initPromise = null;
      throw error;
    }
  }

  /**
   * 获取词汇的最优解释（基于优先级）
   */
  async getWord(word: string): Promise<WordResult | null> {
    const wordLower = word.toLowerCase().trim();
    
    // 检查缓存
    if (this.cache.has(wordLower)) {
      return this.cache.get(wordLower)!;
    }
    
    if (!this.initialized) {
      await this.initialize();
    }
    
    if (!this.db) return null;
    
    try {
      // 查询最优翻译（基于优先级）
      const stmt = this.db.prepare(`
        SELECT 
          w.word,
          ws.translation,
          ws.usphone,
          ws.ukphone,
          ws.pronunciation,
          ws.content_type,
          ws.source_file,
          (
            SELECT GROUP_CONCAT(DISTINCT ws2.source_file)
            FROM word_sources ws2 
            WHERE ws2.word_id = w.id
          ) as all_sources,
          (
            SELECT COUNT(DISTINCT ws3.source_file)
            FROM word_sources ws3 
            WHERE ws3.word_id = w.id
          ) as source_count
        FROM words w
        JOIN word_sources ws ON w.id = ws.word_id
        WHERE w.word = ?
        ORDER BY ws.priority DESC, ws.source_file
        LIMIT 1
      `);
      
      const result = stmt.getAsObject([wordLower]);
      stmt.free();
      
      if (!result.word) {
        this.updateCache(wordLower, null);
        return null;
      }
      
      const wordResult: WordResult = {
        word: result.word,
        translation: result.translation || '',
        usphone: result.usphone || '',
        ukphone: result.ukphone || '',
        pronunciation: result.pronunciation || '',
        content_type: result.content_type || 'meaning',
        level: result.level || 'basic',
        source_file: result.source_file || '',
        all_sources: result.all_sources ? result.all_sources.split(',') : [result.source_file],
        source_count: result.source_count || 1
      };
      
      // 更新缓存
      this.updateCache(wordLower, wordResult);
      
      return wordResult;
      
    } catch (error) {
      console.error('Database query error:', error);
      return null;
    }
  }

  /**
   * 获取词汇的所有来源信息
   */
  async getWordAllSources(word: string): Promise<WordSource[]> {
    const wordLower = word.toLowerCase().trim();
    
    // 检查缓存
    if (this.sourcesCache.has(wordLower)) {
      return this.sourcesCache.get(wordLower)!;
    }
    
    if (!this.initialized) {
      await this.initialize();
    }
    
    if (!this.db) return [];
    
    try {
      const stmt = this.db.prepare(`
        SELECT 
          ws.source_file,
          ws.translation,
          ws.usphone,
          ws.ukphone,
          ws.pronunciation,
          ws.content_type,
          'basic' as level,
          ws.priority
        FROM words w
        JOIN word_sources ws ON w.id = ws.word_id
        WHERE w.word = ?
        ORDER BY ws.priority DESC, ws.source_file
      `);
      
      const results = stmt.all([wordLower]);
      stmt.free();
      
      const sources: WordSource[] = results.map((row: any) => ({
        source_file: row.source_file,
        translation: row.translation || '',
        usphone: row.usphone || '',
        ukphone: row.ukphone || '',
        pronunciation: row.pronunciation || '',
        content_type: row.content_type || 'meaning',
        level: row.level || 'basic',
        priority: row.priority || 0
      }));
      
      // 更新缓存
      this.updateSourcesCache(wordLower, sources);
      
      return sources;
      
    } catch (error) {
      console.error('Database query error:', error);
      return [];
    }
  }

  /**
   * 获取特定词库的词汇解释
   */
  async getWordFromSource(word: string, sourceFile: string): Promise<WordResult | null> {
    const wordLower = word.toLowerCase().trim();
    
    if (!this.initialized) {
      await this.initialize();
    }
    
    if (!this.db) return null;
    
    try {
      const stmt = this.db.prepare(`
        SELECT 
          w.word,
          ws.translation,
          ws.usphone,
          ws.ukphone,
          ws.pronunciation,
          ws.content_type,
          ws.source_file
        FROM words w
        JOIN word_sources ws ON w.id = ws.word_id
        WHERE w.word = ? AND ws.source_file = ?
      `);
      
      const result = stmt.getAsObject([wordLower, sourceFile]);
      stmt.free();
      
      if (!result.word) return null;
      
      return {
        word: result.word,
        translation: result.translation || '',
        usphone: result.usphone || '',
        ukphone: result.ukphone || '',
        pronunciation: result.pronunciation || '',
        content_type: result.content_type || 'meaning',
        level: result.level || 'basic',
        source_file: result.source_file || ''
      };
      
    } catch (error) {
      console.error('Database query error:', error);
      return null;
    }
  }

  async hasTranslation(word: string): Promise<boolean> {
    const result = await this.getWord(word);
    return result !== null && result.translation.length > 0;
  }

  async getTranslation(word: string): Promise<string[]> {
    const result = await this.getWord(word);
    if (!result || !result.translation) return [];
    
    // 处理多个翻译（用分号分隔）
    return result.translation.split(';').map(t => t.trim()).filter(t => t.length > 0);
  }

  /**
   * 获取词汇的所有不同翻译（来自不同词库）
   */
  async getAllTranslations(word: string): Promise<Map<string, string>> {
    const sources = await this.getWordAllSources(word);
    const translations = new Map<string, string>();
    
    sources.forEach(source => {
      if (source.translation) {
        translations.set(source.source_file, source.translation);
      }
    });
    
    return translations;
  }

  async getUSPronunciation(word: string): Promise<string | null> {
    const result = await this.getWord(word);
    return result?.usphone || null;
  }

  // 批量查询，提高性能
  async getWords(words: string[]): Promise<Map<string, WordResult | null>> {
    const results = new Map<string, WordResult | null>();
    
    // 先从缓存获取
    const uncachedWords: string[] = [];
    for (const word of words) {
      const wordLower = word.toLowerCase().trim();
      if (this.cache.has(wordLower)) {
        results.set(word, this.cache.get(wordLower)!);
      } else {
        uncachedWords.push(wordLower);
      }
    }
    
    // 批量查询未缓存的词
    if (uncachedWords.length > 0) {
      if (!this.initialized) await this.initialize();
      if (!this.db) return results;
      
      try {
        const placeholders = uncachedWords.map(() => '?').join(',');
        const stmt = this.db.prepare(`
          SELECT 
            w.word,
            ws.translation,
            ws.usphone,
            ws.ukphone,
            ws.pronunciation,
            ws.content_type,
            ws.source_file,
            (
              SELECT GROUP_CONCAT(DISTINCT ws2.source_file)
              FROM word_sources ws2 
              WHERE ws2.word_id = w.id
            ) as all_sources,
            (
              SELECT COUNT(DISTINCT ws3.source_file)
              FROM word_sources ws3 
              WHERE ws3.word_id = w.id
            ) as source_count
          FROM words w
          JOIN word_sources ws ON w.id = ws.word_id
          WHERE w.word IN (${placeholders})
            AND ws.priority = (
              SELECT MAX(ws4.priority)
              FROM word_sources ws4
              WHERE ws4.word_id = w.id
            )
          GROUP BY w.word
        `);
        
        const queryResults = stmt.all(uncachedWords);
        stmt.free();
        
        // 处理查询结果
        queryResults.forEach((row: any) => {
          const wordResult: WordResult = {
            word: row.word,
            translation: row.translation || '',
            usphone: row.usphone || '',
            ukphone: row.ukphone || '',
            pronunciation: row.pronunciation || '',
            content_type: row.content_type || 'meaning',
            level: row.level || 'basic',
            source_file: row.source_file || '',
            all_sources: row.all_sources ? row.all_sources.split(',') : [],
            source_count: row.source_count || 1
          };
          
          // 找到对应的原始单词
          const originalWord = words.find(w => w.toLowerCase().trim() === row.word);
          if (originalWord) {
            results.set(originalWord, wordResult);
            this.updateCache(row.word, wordResult);
          }
        });
        
        // 对于未找到的词，缓存null结果
        uncachedWords.forEach(wordLower => {
          const originalWord = words.find(w => w.toLowerCase().trim() === wordLower);
          if (originalWord && !results.has(originalWord)) {
            results.set(originalWord, null);
            this.updateCache(wordLower, null);
          }
        });
        
      } catch (error) {
        console.error('Batch query error:', error);
      }
    }
    
    return results;
  }

  private updateCache(word: string, result: WordResult | null): void {
    // LRU缓存策略
    if (this.cache.size >= this.maxCacheSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey !== undefined) {
        this.cache.delete(firstKey);
      }
    }
    
    this.cache.set(word, result);
  }

  private updateSourcesCache(word: string, sources: WordSource[]): void {
    // LRU缓存策略
    if (this.sourcesCache.size >= this.maxCacheSize / 2) {
      const firstKey = this.sourcesCache.keys().next().value;
      if (firstKey !== undefined) {
        this.sourcesCache.delete(firstKey);
      }
    }
    
    this.sourcesCache.set(word, sources);
  }

  // 获取统计信息
  async getStats(): Promise<{ 
    uniqueWords: number, 
    totalSources: number, 
    cacheSize: number, 
    memoryUsage: string,
    multiSourceWords?: number
  }> {
    let uniqueWords = 0;
    let totalSources = 0;
    let multiSourceWords = 0;
    
    if (this.initialized && this.db) {
      try {
        const wordsResult = this.db.exec('SELECT COUNT(*) as count FROM words')[0];
        uniqueWords = wordsResult.values[0][0];
        
        const sourcesResult = this.db.exec('SELECT COUNT(*) as count FROM word_sources')[0];
        totalSources = sourcesResult.values[0][0];
        
        const multiResult = this.db.exec(`
          SELECT COUNT(*) as count FROM (
            SELECT word_id, COUNT(*) as source_count 
            FROM word_sources 
            GROUP BY word_id 
            HAVING source_count > 1
          )
        `)[0];
        multiSourceWords = multiResult.values[0][0];
        
      } catch (error) {
        console.error('Stats query error:', error);
      }
    }
    
    const totalCacheSize = this.cache.size + this.sourcesCache.size;
    
    return {
      uniqueWords,
      totalSources,
      cacheSize: totalCacheSize,
      memoryUsage: `~${(totalCacheSize * 250 / 1024).toFixed(1)}KB`, // 估算缓存内存使用
      multiSourceWords
    };
  }

  // 清理资源
  destroy(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
    
    this.cache.clear();
    this.sourcesCache.clear();
    this.initialized = false;
    this.initPromise = null;
    
    console.log('🗑️  Vocabulary database V2 destroyed');
  }

  // 预热缓存（可选）
  async warmupCache(commonWords: string[]): Promise<void> {
    console.log(`🔥 Warming up cache with ${commonWords.length} common words...`);
    await this.getWords(commonWords);
    console.log(`✅ Cache warmed up: ${this.cache.size} words cached`);
  }
}

// 创建单例实例
export const vocabularyDBV2 = new VocabularyDBV2();

// 导出类型
export type { WordResult, WordSource };